import { useCallback, useEffect, useState } from "react";
import { brandApi } from "../lib/brand-api";
import { useToast } from "../components/ui/Toast";
import BrandReview from "./BrandReview";
import { CrossSourceSearch } from "./BrandAccountDetail";

/**
 * Accounts — the single destination that replaced My Chains, Mappings and My
 * Review. This is the index: every parent account with its coverage at a
 * glance, the cross-source search, and the match queue inline rather than
 * parked behind its own tab. Drill into an account for the full workspace.
 */

interface Cell {
  status: "no_data" | "zero" | "spend";
  amount_usd: number | null;
}
interface CoverageStore {
  included: boolean;
  platforms: { dutchie: Cell; iheartjane: Cell };
}
interface CoverageAccount {
  tenant_chain_id: string;
  name: string;
  counts: {
    canonical_locations: number;
    included_members: number;
    wtb_confirmed: number;
    adspend_confirmed: number;
  };
  account_level: { surfside: Cell };
  stores: CoverageStore[];
}

function money(n: number | null): string {
  if (n == null) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

/** Total known spend on an account this period: store cells + the account-level line. */
function accountSpend(a: CoverageAccount): { total: number; anyData: boolean } {
  let total = 0;
  let anyData = false;
  for (const s of a.stores) {
    for (const cell of [s.platforms.dutchie, s.platforms.iheartjane]) {
      if (cell.status !== "no_data") {
        anyData = true;
        total += cell.amount_usd ?? 0;
      }
    }
  }
  if (a.account_level.surfside.status !== "no_data") {
    anyData = true;
    total += a.account_level.surfside.amount_usd ?? 0;
  }
  return { total, anyData };
}

export default function BrandAccounts({ slug }: { slug: string }) {
  const toast = useToast();
  const [accounts, setAccounts] = useState<CoverageAccount[]>([]);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [period, setPeriod] = useState<{ period_start: string; period_end: string } | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await brandApi.get(`/api/brand/${encodeURIComponent(slug)}/accounts/coverage`);
      setAccounts(r.accounts ?? []);
      setUnresolvedCount((r.unresolved ?? []).length);
      setPeriod(r.period ?? null);
    } catch (err) {
      toast.push(`Failed to load accounts: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [slug, toast]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Accounts</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-600">
            Every parent account, what it spent on each ad platform, and everything you can correct — the store roster,
            the matches, and anything we haven't linked yet.
          </p>
        </div>
        {period && (
          <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600">
            Spend period: <span className="font-medium text-gray-900">{period.period_start} → {period.period_end}</span>
          </div>
        )}
      </div>

      {unresolvedCount > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">{unresolvedCount} ad-spend {unresolvedCount === 1 ? "row" : "rows"}</span> couldn't
          be tied to a store. They're listed on the account they belong to, or searchable below.
        </div>
      )}

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">Loading accounts…</div>
      ) : accounts.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center">
          <p className="text-sm font-medium text-gray-700">No accounts yet.</p>
          <p className="mt-1 text-xs text-gray-500">
            Accounts appear once your Customer Master or Where-to-Buy upload has been matched.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <ul className="divide-y divide-gray-100">
            {accounts.map((a) => {
              const spend = accountSpend(a);
              const included = a.stores.filter((s) => s.included).length;
              return (
                <li key={a.tenant_chain_id}>
                  <a
                    href={`#/brand/${encodeURIComponent(slug)}/accounts/${encodeURIComponent(a.tenant_chain_id)}`}
                    className="flex flex-wrap items-center gap-4 px-4 py-3.5 hover:bg-gray-50"
                  >
                    <div className="min-w-[220px] flex-1">
                      <div className="font-medium text-gray-900">{a.name}</div>
                      <div className="mt-0.5 text-xs text-gray-500">
                        {included} included / {a.counts.canonical_locations} canonical
                        {a.counts.wtb_confirmed > 0 ? ` · ${a.counts.wtb_confirmed} WTB` : ""}
                        {a.counts.adspend_confirmed > 0 ? ` · ${a.counts.adspend_confirmed} ad-spend` : ""}
                      </div>
                    </div>
                    <div className="text-right">
                      {spend.anyData ? (
                        <span className="font-mono text-sm font-medium text-amber-800">{money(spend.total)}</span>
                      ) : (
                        <span className="text-sm text-gray-400" title="No platform reported anything for this account in this period">
                          no data
                        </span>
                      )}
                      <span className="mt-0.5 block text-[10px] uppercase tracking-wide text-gray-400">this period</span>
                    </div>
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <CrossSourceSearch slug={slug} />

      <div>
        <h2 className="text-sm font-semibold text-gray-900">Matches to review</h2>
        <p className="mt-0.5 mb-3 text-xs text-gray-600">
          Rows from your uploads we matched automatically, and the ones we weren't confident enough to decide alone.
        </p>
        <BrandReview slug={slug} />
      </div>
    </div>
  );
}
